"use strict";
/** *******************************************************************************************************************
  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
  
  Licensed under the Apache License, Version 2.0 (the "License").
  You may not use this file except in compliance with the License.
  You may obtain a copy of the License at
  
      http://www.apache.org/licenses/LICENSE-2.0
  
  Unless required by applicable law or agreed to in writing, software
  distributed under the License is distributed on an "AS IS" BASIS,
  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
  See the License for the specific language governing permissions and
  limitations under the License.                                                                              *
 ******************************************************************************************************************** */
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importDefault(require("react"));
const clsx_1 = __importDefault(require("clsx"));
const uuid_1 = require("uuid");
const styles_1 = require("@material-ui/core/styles");
const Link_1 = __importDefault(require("@material-ui/core/Link"));
const Select_1 = __importDefault(require("@material-ui/core/Select"));
const MenuItem_1 = __importDefault(require("@material-ui/core/MenuItem"));
const ListSubheader_1 = __importDefault(require("@material-ui/core/ListSubheader"));
const LoadingIndicator_1 = __importDefault(require("../LoadingIndicator"));
const StatusIndicator_1 = __importDefault(require("../StatusIndicator"));
const Button_1 = require("../Button");
const useUniqueId_1 = __importDefault(require("../../hooks/useUniqueId"));
const useStyles = styles_1.makeStyles((theme) => ({
    iconMenuItem: {
        marginRight: theme.spacing(0.4),
    },
    groupMenuItem: {
        paddingLeft: theme.spacing(3),
    },
    subheaderMenuItem: {
        display: 'flex',
        alignItems: 'center',
        pointerEvents: 'none',
    },
    recoveryLink: {
        pointerEvents: 'auto',
    },
    errorText: {
        marginRight: theme.spacing(0.4),
    },
    placeholder: {
        display: 'none',
    },
}));
const renderOptions = (options, classes, renderOption, disabled = false, groupItem = false) => {
    return options.map((option) => {
        if (option.options) {
            const key = typeof option.label === 'string' ? option.label : uuid_1.v4();
            return [
                react_1.default.createElement(ListSubheader_1.default, { key: key }, option.label),
                ...renderOptions(option.options, classes, renderOption, option.disabled, true),
            ];
        }
        else {
            const key = option.value || uuid_1.v4();
            return (react_1.default.createElement(MenuItem_1.default, { key: key, value: option.value, disabled: option.disabled || disabled, className: clsx_1.default({ [classes.groupMenuItem]: groupItem }) },
                option.iconName && (react_1.default.createElement("span", { className: classes.iconMenuItem },
                    react_1.default.createElement(Button_1.ButtonIcon, { type: option.iconName }))),
                (renderOption === null || renderOption === void 0 ? void 0 : renderOption(option)) || option.label || option.value));
        }
    });
};
const muiSelectProps = ({ ariaDescribedby, ariaLabelledby, ariaRequired, controlId, disabled, invalid, label, onBlur, onChange, onFocus, selectedOption, placeholder, }) => {
    return Object.assign(Object.assign(Object.assign(Object.assign({ disabled, error: invalid, id: controlId, onChange,
        placeholder, onClose: onBlur, onOpen: onFocus, value: (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.value) || '' }, (ariaLabelledby && { 'aria-labelledby': ariaLabelledby })), (ariaDescribedby && { 'aria-describedby': ariaDescribedby })), (label && { 'aria-label': label })), (ariaRequired && { 'aria-required': ariaRequired }));
};
/**
 * Select components are used for collecting user provided information from a list of options.
 */
const Select = (_a) => {
    var { ariaDescribedby, ariaLabelledby, ariaRequired, disabled = false, empty, errorText, invalid = false, label, loadingText, onBlur, onChange, onFocus, onRecoveryClick, options = [], recoveryText, renderOption, placeholder, selectedOption, statusType = 'finished' } = _a, props = __rest(_a, ["ariaDescribedby", "ariaLabelledby", "ariaRequired", "disabled", "empty", "errorText", "invalid", "label", "loadingText", "onBlur", "onChange", "onFocus", "onRecoveryClick", "options", "recoveryText", "renderOption", "placeholder", "selectedOption", "statusType"]);
    const classes = useStyles();
    const controlId = useUniqueId_1.default(props.controlId);
    const onRecoveryClickHandler = (event) => {
        event.preventDefault();
        onRecoveryClick === null || onRecoveryClick === void 0 ? void 0 : onRecoveryClick(event);
        event.stopPropagation();
    };
    const testId = props['data-testid'] || 'select';
    return (react_1.default.createElement(Select_1.default, Object.assign({ variant: "outlined", displayEmpty: true, fullWidth: true }, muiSelectProps({
        ariaDescribedby,
        ariaLabelledby,
        ariaRequired,
        controlId,
        disabled,
        invalid,
        label,
        onBlur,
        onChange,
        onFocus,
        selectedOption,
    }), { MenuProps: {
            anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
            },
            transitionDuration: 0,
            getContentAnchorEl: null,
        }, "data-testid": testId }),
        placeholder && (react_1.default.createElement(MenuItem_1.default, { key: "__placeholder__", value: "", disabled: true, className: classes.placeholder }, placeholder)),
        empty && !options.length && (react_1.default.createElement(ListSubheader_1.default, { key: "__status__", className: classes.subheaderMenuItem }, empty)),
        statusType !== 'finished' && (react_1.default.createElement(ListSubheader_1.default, { className: classes.subheaderMenuItem, key: "__status__" },
            statusType === 'loading' && react_1.default.createElement(LoadingIndicator_1.default, { label: loadingText }),
            statusType === 'error' && (react_1.default.createElement(react_1.default.Fragment, null,
                react_1.default.createElement("span", { className: classes.errorText },
                    react_1.default.createElement(StatusIndicator_1.default, { statusType: "negative" }, errorText)),
                recoveryText && (react_1.default.createElement(Link_1.default, { href: "#", onClick: onRecoveryClickHandler, className: classes.recoveryLink }, recoveryText)))))),
        statusType === 'finished' && renderOptions(options, classes, renderOption)));
};
exports.default = Select;
